import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ZefReactiveComponent, escapeEnvContent } from '@zerops/zef/core';
import { selectZefDialogState } from '@zerops/zef/dialog';
import { zefDialogClose } from '@zerops/zef/dialog';
import {
  Subject,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  withLatestFrom
} from 'rxjs';
import { AppState } from '@zerops/zerops/app';
import { FEATURE_NAME } from './pipeline-retrigger-dialog.constant';
import { PipelineRetriggerForm } from './pipeline-retrigger-dialog.form';
import { formValueOnValid } from '@zerops/zef/forms';
import { ServiceStackEntity, triggerRedeploy } from '@zerops/zerops/core/service-stack-base';
import { AppVersionEntity } from '@zerops/zerops/core/app-version-base';
import { AppVersion } from '@zerops/models/app-version';
import { pipelineDetailDialogOpen } from '../pipeline-detail-dialog';
import {
  UserDataEntity,
  revealUserData,
  clearRevealedUserData,
  selectRevealedUserDataContent
} from '@zerops/zerops/core/user-data-base';
import { UserData, UserDataTypes } from '@zerops/models/user-data';

@Component({
  selector: 'z-pipeline-retrigger-dialog',
  templateUrl: './pipeline-retrigger-dialog.feature.html',
  styleUrl: './pipeline-retrigger-dialog.feature.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PipelineRetriggerDialogFeature extends ZefReactiveComponent {

  // # Deps
  #store = inject<Store<AppState>>(Store);
  #form = inject(PipelineRetriggerForm);
  #serviceStackEntity = inject(ServiceStackEntity);
  #appVersionEntity = inject(AppVersionEntity);
  #userDataEntity = inject(UserDataEntity);

  // # Event Streams
  onClose$ = new Subject<void>();
  onSubmit$ = new Subject<void>();
  onPrefillEnvs$ = new Subject<void>();
  onPrefillSecrets$ = new Subject<void>();
  onOpenPipelineDetailDialog$ = new Subject<{ appVersion: AppVersion; }>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  actionKey = triggerRedeploy.type;

  // -- async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => data?.state));
  id$ = this.dialogState$.pipe(map((data) => data?.meta));
  service$ = this.id$.pipe(
    filter((d) => !!d),
    distinctUntilChanged(),
    switchMap((d) => this.#serviceStackEntity.entityById$(d).pipe(filter((d) => !!d)))
  );
  activeAppVersionId$ = this.service$.pipe(
    map((d) => d.activeAppVersion?.id),
    filter((d) => !!d)
  );
  userData$ = this.service$.pipe(
    switchMap((service) => this.#userDataEntity.list$({
      name: this.key,
      id: service.id
    }).pipe(filter((d) => !!d?.length)))
  );
  activeAppVersion$ = this.activeAppVersionId$.pipe(
    withLatestFrom(this.service$),
    switchMap(([ id, service ]) => this.#appVersionEntity.list$({
      name: this.key,
      id: service.id
    }).pipe(
      filter((d) => !!d?.length),
      map((d) => d.find((itm) => itm.id === id)),
      filter((d) => !!d)
    ))
  );
  formState$ = this.#form.state$;
  formValue$ = this.#form.value$;
  revealedContent$ = this.#store.pipe(select(selectRevealedUserDataContent));

  // # State resolver
  state = this.$connect({
    open: this.open$,
    formState: this.formState$,
    activeAppVersion: this.activeAppVersion$
  });

  constructor() {
    super();

    // # Dispatcher
    this.$dispatchActions([

      this.onClose$.pipe(
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      ),

      this.onSubmit$.pipe(
        formValueOnValid(this.#form),
        withLatestFrom(this.id$),
        map(([ data, id ]) => {
          return triggerRedeploy({
            id,
            envVarsFile: data.envVarsFile,
            envSecretsFile: data.envSecretsFile
          }, { type: 'pop' });
        })
      ),

      this.onPrefillEnvs$.pipe(
        withLatestFrom(this.userData$, this.service$),
        map(([ _, userData, service ]) => {
          const value = userData
            ?.filter((itm) => itm.type === UserDataTypes.User && itm.editable && !itm.sensitive && itm.serviceStackId === service.id)
            ?.reduce((str, itm) => {
              const formattedContent = escapeEnvContent(itm.content);
              return str + `${itm.key}=${formattedContent}\n`;
            }, '');
            if (!value) {
            return false as any;
          }
          return this.#form.setValue(
            'envVarsFile',
            value
          );
        }),
        filter((d) => !!d)
      ),

      // reveal each existing secret (sudo re-auth may fire) so its real value can
      // be dumped into the secrets field
      this.onPrefillSecrets$.pipe(
        withLatestFrom(this.userData$, this.service$, this.revealedContent$),
        mergeMap(([ _, userData, service, revealed ]) =>
          this.#secrets(userData, service.id)
            .filter((itm) => revealed[itm.id] === undefined)
            .map((itm) => revealUserData({ id: itm.id }))
        )
      ),

      // once all requested secrets are revealed, serialize them into the secrets
      // code-field (waits through any sudo re-auth round-trip)
      this.onPrefillSecrets$.pipe(
        switchMap(() => this.revealedContent$.pipe(
          withLatestFrom(this.userData$, this.service$),
          filter(([ revealed, userData, service ]) => {
            const secrets = this.#secrets(userData, service.id);
            return secrets.length > 0 && secrets.every((itm) => revealed[itm.id] !== undefined);
          }),
          take(1),
          map(([ revealed, userData, service ]) => this.#form.setValue(
            'envSecretsFile',
            this.#secrets(userData, service.id).reduce(
              (str, itm) => str + `${itm.key}=${escapeEnvContent(revealed[itm.id])}\n`,
              ''
            )
          ))
        ))
      ),

      // clear revealed secret values when the dialog closes
      this.onClose$.pipe(
        map(() => clearRevealedUserData())
      ),

      this.onOpenPipelineDetailDialog$.pipe(
        map(({ appVersion }) => pipelineDetailDialogOpen({ appVersion }))
      )

    ]);
  }

  #secrets(userData: UserData[], serviceStackId: string) {
    return userData.filter((itm) =>
      itm.type === UserDataTypes.User
      && itm.editable
      && itm.sensitive
      && itm.serviceStackId === serviceStackId
    );
  }

}
